import {
    useState,
    forwardRef,
    useImperativeHandle,
    createRef,
} from 'react'

import DateSelector from '../DateSelector'
import StartEndDateCoupleSelector from '../StartEndDateCoupleSelector'
import TextInput from '../TextInput'
import EmailInputForm from '../EmailInputForm'
import LocationaInput from '../LocationInput'
import FullNameInput from '../FullNameInput'
import SsnInput from '../SsnInput'
import AddressFields from '../AddressFields'
import AddressAndDateInput from '../AddressAndDateInput'
import DateFromToMonthYearInput from '../DateFromToMonthYearInput'
import ContactInformation from '../ContactInformation'
import ProofOfIdentity from '../ProofOfIdentity'
import DegreeEarned from '../DegreeEarned'
import OptionInput from '../OptionInput'
import HasAnotherNameInput from '../HasAnotherNameInput'
import FullNameAndAkaInput from '../FullNameAndAkaInput'
import MonthYearElement from '../MonthYearElement'
import AddPhoneNumber from '../AddPhoneNumber'
import EducationCompleteAndDateInput from '../EducationCompleteAndDateInput'
import IsUnemploymentAndDataInput from '../IsUnemploymentAndDataInput'
import CurrentlyEmployedAndConsentInput from '../CurrentlyEmployedAndConsentInput'
import CurrentAddress from '../CurrentAddress'
import SsnOrIdentityInput from '../SsnOrIdentityInput'
import EmployeeReference from '../EmployeeReference'
import PhoneNumberInput from '../PhoneNumberInput'
import TextAreaInput from '../TextAreaInput'
import AddressAdditionalField from '../AddressAdditionalField'
import CountryFields from '../CountryFields'
import MediaInput from '../MediaInput'
import LinkedinProfileUrlInput from '../LinkedinProfileUrlInput'
import MediaTextInput from '../MediaTextInput'


const DataElement = forwardRef(({ attributes, data, readOnly }, ref) => {

    const elementData = data || {}

    const elementRefs = attributes.map(_ => createRef())
    const [, setError] = useState(false)


    useImperativeHandle(ref, () => ({
        validateAndGetData: () => {
            const elementResults = elementRefs.map((elementRef) => elementRef.current.validateAndGetData())
            const error = elementResults.some((result) => !result.isValid)
            setError(error)

            const isValid = !error

            const result = {}

            for (const [index, attribute] of attributes.entries()) {
                result[attribute.key] = elementResults[index].value
            }

            return {
                isValid,
                value: result,
            }
        },
    }))

    return (
        <div>

            {elementRefs.map((elementRef, index) => (

                <div key={index}>
                    {
                        ["text", "string"].includes(attributes[index].type) && (
                            <TextInput
                                title={attributes[index].title}
                                ref={elementRef}
                                data={elementData[attributes[index].key]}
                                disabled={readOnly}
                                isMandatory={attributes[index].isMandatory}
                            />
                        )
                    }

                    {
                        attributes[index].type === "linkedin_profile_url" && (
                            <LinkedinProfileUrlInput
                                ref={elementRef}
                                data={elementData[attributes[index].key]}
                                disabled={readOnly}
                                isMandatory={attributes[index].isMandatory}
                            />
                        )
                    }

                    {
                        attributes[index].type === "text_area" && (
                            <TextAreaInput
                                title={attributes[index].title}
                                ref={elementRef}
                                data={elementData[attributes[index].key]}
                                disabled={readOnly}
                                isMandatory={attributes[index].isMandatory}
                            />
                        )
                    }

                    {
                        attributes[index].type === "date" && (
                            <DateSelector title={attributes[index].title} ref={elementRef} data={elementData[attributes[index].key]} readOnly={readOnly} />
                        )
                    }

                    {
                        attributes[index].type === "start_end_date_couple" && (
                            <StartEndDateCoupleSelector title={attributes[index].title} ref={elementRef} data={elementData[attributes[index].key]} readOnly={readOnly} />
                        )
                    }
                    {
                        attributes[index].type === "email" && (
                            <EmailInputForm title={attributes[index].title} ref={elementRef} data={elementData[attributes[index].key]} readOnly={readOnly} />
                        )
                    }
                    {
                        attributes[index].type === "phone_number" && (
                            <AddPhoneNumber
                                title={attributes[index].title}
                                ref={elementRef}
                                data={elementData[attributes[index].key]}
                                readOnly={readOnly}
                            />
                        )
                    }
                    {
                        attributes[index].type === "location" && (
                            <LocationaInput title={attributes[index].title} ref={elementRef} data={elementData[attributes[index].key]} readOnly={readOnly} />
                        )
                    }
                    {
                        attributes[index].type === "full_name" && (
                            <FullNameInput title={attributes[index].title} ref={elementRef} data={elementData[attributes[index].key]} readOnly={readOnly} />
                        )
                    }
                    {
                        attributes[index].type === "full_name_and_aka" && (
                            <FullNameAndAkaInput title={attributes[index].title} ref={elementRef} data={elementData[attributes[index].key]} readOnly={readOnly} />
                        )
                    }
                    {
                        attributes[index].type === "ssn" && (
                            <SsnInput title={attributes[index].title} ref={elementRef} data={elementData[attributes[index].key]} readOnly={readOnly} />
                        )
                    }
                    {
                        attributes[index].type === "address" && (
                            <AddressFields title={attributes[index].title} ref={elementRef} data={elementData[attributes[index].key]} readOnly={readOnly} />
                        )
                    }
                    {
                        attributes[index].type === "address_and_date" && (
                            <AddressAndDateInput
                                title={attributes[index].title}
                                ref={elementRef}
                                data={elementData[attributes[index].key]}
                                attributes={attributes[index].attributes}
                                readOnly={readOnly}
                            />
                        )
                    }
                    {
                        attributes[index].type === "current_address" && (
                            <CurrentAddress
                                title={attributes[index].title}
                                ref={elementRef}
                                data={elementData[attributes[index].key]}
                                readOnly={readOnly}
                            />
                        )
                    }
                    {
                        attributes[index].type === "date_from_to_month_year" && (
                            <DateFromToMonthYearInput
                                title={attributes[index].title}
                                ref={elementRef}
                                data={elementData[attributes[index].key]}
                                attributes={attributes[index].attributes}
                                readOnly={readOnly}
                                fromFinalValue={attributes[index].fromFinalValue}
                                toFinalValue={attributes[index].toFinalValue}
                                maxFutureYearsFromNow={0}
                            />
                        )
                    }
                    {
                        attributes[index].type === "contact_information" && (
                            <ContactInformation
                                title={attributes[index].title}
                                ref={elementRef}
                                data={elementData[attributes[index].key]}
                                attributes={attributes[index].attributes}
                                readOnly={readOnly}
                            />
                        )
                    }
                    {
                        attributes[index].type === "proof_of_identity" && (
                            <ProofOfIdentity
                                title={attributes[index].title}
                                ref={elementRef}
                                data={elementData[attributes[index].key]}
                                attributes={attributes[index].attributes}
                                readOnly={readOnly}
                            />
                        )
                    }
                    {
                        attributes[index].type === "date_month_year" && (
                            <MonthYearElement
                                title={attributes[index].title}
                                ref={elementRef}
                                data={
                                    attributes[index].finalValue === "now" ?
                                        {
                                            month: String(new Date().getMonth() + 1).padStart(2, '0'),
                                            year: String(new Date().getFullYear()),
                                        } :
                                        elementData[attributes[index].key]
                                }
                                readOnly={readOnly || attributes[index].finalValue === "now"}
                            />
                        )
                    }
                    {
                        attributes[index].type === "degree_earned" && (
                            <DegreeEarned
                                title={attributes[index].title}
                                ref={elementRef}
                                data={elementData[attributes[index].key]}
                                attributes={attributes[index].attributes}
                                readOnly={readOnly}
                                isMandatory={attributes[index].isMandatory}
                            />
                        )
                    }
                    {
                        attributes[index].type === "option" && (
                            <OptionInput
                                title={attributes[index].title}
                                ref={elementRef}
                                data={elementData[attributes[index].key]}
                                options={attributes[index].options}
                                readOnly={readOnly}
                            />
                        )
                    }
                    {
                        attributes[index].type === "has_another_name" && (
                            <HasAnotherNameInput
                                title={attributes[index].title}
                                ref={elementRef}
                                data={elementData[attributes[index].key]}
                                readOnly={readOnly}
                            />
                        )
                    }
                    {
                        attributes[index].type === "is_edication_complate_and_date" && (
                            <EducationCompleteAndDateInput
                                title={attributes[index].title}
                                ref={elementRef}
                                data={elementData[attributes[index].key]}
                                readOnly={readOnly}
                            />
                        )
                    }
                    {
                        attributes[index].type === "is_unemployed_and_data" && (
                            <IsUnemploymentAndDataInput
                                title={attributes[index].title}
                                ref={elementRef}
                                data={elementData[attributes[index].key]}
                                attributes={attributes[index].attributes}
                                readOnly={readOnly}
                            />
                        )
                    }
                    {
                        attributes[index].type === "currently_employed_and_contact_consent" && (
                            <CurrentlyEmployedAndConsentInput
                                title={attributes[index].title}
                                ref={elementRef}
                                data={elementData[attributes[index].key]}
                                attributes={attributes[index].attributes}
                                readOnly={readOnly}
                            />
                        )
                    }
                    {
                        attributes[index].type === "ssn_or_identity" && (
                            <SsnOrIdentityInput
                                title={attributes[index].title}
                                ref={elementRef}
                                data={elementData[attributes[index].key]}
                                attributes={attributes[index].attributes}
                                readOnly={readOnly}
                            />
                        )
                    }
                    {
                        attributes[index].type === "professional_reference_item_attributes" && (
                            <EmployeeReference
                                ref={elementRef}
                                data={elementData[attributes[index].key]}
                                attributes={attributes[index].attributes}
                                readOnly={readOnly}
                            />

                        )
                    }
                    {
                        attributes[index].type === "phone_number_item" && (
                            <PhoneNumberInput
                                title={attributes[index].title}
                                ref={elementRef}
                                data={elementData[attributes[index].key]}
                                readOnly={readOnly}
                            />
                        )
                    }
                    {
                        attributes[index].type === "address_additional_field" && (
                            <AddressAdditionalField
                                title={attributes[index].title}
                                ref={elementRef}
                                data={elementData[attributes[index].key]}
                                attributes={attributes[index].attributes}
                                readOnly={readOnly}
                            />
                        )
                    }
                    {
                        attributes[index].type === "country_fields" && (
                            <CountryFields
                                title={attributes[index].title}
                                ref={elementRef}
                                data={elementData[attributes[index].key]}
                                attributes={attributes[index].attributes}
                                readOnly={readOnly}
                            />
                        )
                    }
                    {
                        attributes[index].type === "media" && (
                            <MediaInput
                                title={attributes[index].title}
                                ref={elementRef}
                                data={elementData[attributes[index].key]}
                                disabled={readOnly}
                                isMandatory={attributes[index].isMandatory}
                                readOnly={readOnly}
                            />
                        )
                    }
                    {
                        attributes[index].type === "media_text" && (
                            <MediaTextInput
                                title={attributes[index].title}
                                ref={elementRef}
                                data={elementData[attributes[index].key]}
                                disabled={readOnly}
                                isMandatory={attributes[index].isMandatory}
                                readOnly={readOnly}
                            />
                        )
                    }
                    {
                        attributes[index].type === "list" && (
                            <OptionInput
                                title={attributes[index].title}
                                ref={elementRef}
                                data={elementData[attributes[index].key]}
                                options={Object.entries(attributes[index].values).map(([key, title]) => ({ title, key }))}
                                readOnly={readOnly}
                                outputType={attributes[index].outputType}
                            />
                        )
                    }

                </div>

            ))}

        </div>
    )

})

export default DataElement
